
/**
 * C2wContactUsHome Component
 * @Author Govind Dindewad
 * This component provides a contact form for users to submit their details and inquiries. 
 * It includes fields for name, email, phone number, message, and more. The form validates 
 * user inputs and submits the data to a Google Apps Script endpoint.
 * 
 * Features:
 * - Real-time input validation for email and phone number
 * - Displays success and error messages
 * - Pop-up notification upon successful submission
 * 
 * Dependencies:
 * - React (useState)
 * - Framer Motion (for animation support)
 * 
 * @component
 */
import React, { useState, useEffect } from 'react';
import "./contactUsHome.css";
import data from '../setting/enquiry/enquiry.json';

function C2wContactUsHome() {
    // State for storing form data

    const formatDateTime = (dateTime) => {
        try {
            const date = new Date(dateTime);
            const options = { year: "numeric", month: "2-digit", day: "2-digit" };
            const formattedDateSplit = date.toLocaleDateString("en-GB", options).replace(/\//g, "-").split('-');
            const formattedDate = `${formattedDateSplit[2]}-${formattedDateSplit[1]}-${formattedDateSplit[0]}`
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, "0");
            const period = hours >= 12 ? "PM" : "AM";
            const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${"00"}`;
            return `${formattedDate} ${formattedTime}`;
        } catch (err) {

        }

    };


    const [formData, setFormData] = useState({
        date: formatDateTime(new Date().toISOString()),
        name: "",
        number: "",
        email: "",
        reason: "",
        courseIntrested: "",
        college: "",
        year: "",
        branch: "",
        batches: "",
        message: "",
        reference: "",
        type: "home",
    });

    // Helper function to check if a string is numeric
    function isNumeric(str) {
        if (typeof str !== "string") return false; // Ensure input is a string
        return !isNaN(str) && !isNaN(parseFloat(str));
    }

    // State for tracking form errors
    const [errors, setErrors] = useState({});
    // State to indicate if there is an error in form validation
    const [isError, setIsError] = useState(false);
    // State to indicate if submission is successful
    const [isSuccess, setIsSuccess] = useState(false);
    // State to show/hide pop-up on successful submission
    const [popup, setPopup] = useState(false);

    /**
     * handleChange - Updates the state as user types into input fields
     * @param {Object} e - Event object from input field
     */
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Validate phone number input
        if (name === "number") {
            if (value === '') {
                setFormData((prevData) => ({ ...prevData, [name]: value }));
                return;
            }
            if (!isNumeric(value) || value.length > 10) {
                return; // Restrict non-numeric values or length > 10
            }
        }

        // Clear errors for the current field if any
        if (errors[name]) {
            setErrors((prevData) => ({ ...prevData, [name]: null }));
        }

        // Update form data state
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    /**
     * validate - Validates the form fields
     * @returns {Boolean} - Whether the form data is valid
     */
    const validate = () => {
        let isValid = true;
        let newErrors = {};

        // Validate name
        if (!formData.name) {
            isValid = false;
            newErrors.name = "Name is required.";
        }

        // Validate email
        if (!formData.email) {
            isValid = false;
            newErrors.email = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            isValid = false;
            newErrors.email = "Invalid email format.";
        }

        // Validate phone number
        if (!formData.number) {
            isValid = false;
            newErrors.number = "Phone number is required.";
        } else if (!/^\d{10}$/.test(formData.number)) {
            isValid = false;
            newErrors.number = "Phone number must be 10 digits.";
        }

        // Validate course selection
        if (!formData.courseIntrested) {
            isValid = false;
            newErrors.courseIntrested = "Please select a course option.";
        }

        // Validate message
        if (!formData.message) {
            isValid = false;
            newErrors.message = "Message is required.";
        }

        setIsError(!isValid);
        setErrors(newErrors);
        return isValid;
    };

    /**
     * handleSubmit - Handles form submission, validates the data, and sends it to the server
     * @param {Object} e - Event object from form submission
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSuccess(true);

        if (validate()) {
            const formD = new FormData();
            formD.append('date', formatDateTime(new Date()));
            Object.keys(formData).forEach(key => formD.append(key, formData[key]));

            try {
                const response = await fetch(
                    'https://script.google.com/macros/s/AKfycbzOh_Tj3DBaagd2iYN1CQ0mREBM8n3fLEtjvYfAFXr2yji5FpLBm9AL4A7b1BKYbygt_Q/exec',
                    { method: 'POST', body: formD }
                );

                if (!response.ok) throw new Error('Network error or script issue');

                // Reset form on success
                setIsSuccess(false);
                setFormData({
                    date: formatDateTime(new Date().toISOString()),
                    name: "",
                    number: "",
                    email: "",
                    reason: "",
                    courseIntrested: "",
                    college: "",
                    year: "",
                    branch: "",
                    batches: "",
                    message: "",
                    reference: "",
                    type: "home",
                });
                setErrors({});
                setPopup(true);

                const timer = setTimeout(() => setPopup(false), 3000);
                return () => clearTimeout(timer);
            } catch (error) {
                alert("Not Submitted, Please Try Again");
                setIsSuccess(false);
            }
        } else {
            setIsSuccess(false);
        }
    };

    /**
       * @state windowWidth - Tracks the current width of the browser window.
       * @hook useEffect - Window Resize Listener
       * @author Vishal Lohbande
       * Description:
       * - Initializes `windowWidth` state with the current width of the browser window.
       * - Adds an event listener to update the state whenever the window is resized.
       *
       * Behavior:
       * - `handleResize` updates the `windowWidth` state using `window.innerWidth`.
       * - The resize listener is attached once on component mount.
       * - On unmount, the event listener is properly cleaned up to prevent memory leaks.
       *
       * Use Case:
       * - Responsive UI adjustments (e.g., conditional rendering based on screen width).
       * - Dynamic layout shifts or breakpoints detection in React components.
       *
       * Notes:
       * - This setup ensures that UI can respond in real-time to screen size changes.
       */
          const [windowWidth, setWindowWidth] = useState(window.innerWidth);
          useEffect(() => {
              const handleResize = () => {
                  setWindowWidth(window.innerWidth);
              };
      
              window.addEventListener('resize', handleResize);
              return () => window.removeEventListener('resize', handleResize);
          }, []);

    return (
        <div className='C2wContactUsHome'>
            <div className='C2wContactUsHome_container'>
                {/* Left Section: Information */}
                <div className='C2wContactUsHome_info_container'>
                    {/* Header Information */}
                    <div className='C2wContactUsHome_selfInfo_container'>
                        <div className='C2wContactUsHome_selfInfo_text'>
                            <h4>WE’RE HERE TO HELP YOU</h4>
                            <div className='C2wContactUsHome_selfInfo_heading'>Talk To Our <br /><span>Experts</span></div>
                            <p>You can share your details with us <br />to receive updates on events, <br />batches, and seminars.</p>
                        </div>

                        {/* Contact Details */}
                        <div className='C2wContactUsHome_selfInfo_details'>
                            {/* Email Section */}
                            <div className='C2wContactUsHome_selfInfo_contact_container'>
                                <svg width="34" height="28" viewBox="0 0 34 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66536 27.3327C2.7487 27.3327 1.96425 27.0066 1.31203 26.3543C0.659809 25.7021 0.333142 24.9171 0.332031 23.9993V3.99935C0.332031 3.08268 0.658698 2.29824 1.31203 1.64602C1.96536 0.993793 2.74981 0.667127 3.66536 0.666016H30.332C31.2487 0.666016 32.0337 0.992682 32.687 1.64602C33.3404 2.29935 33.6665 3.08379 33.6654 3.99935V23.9993C33.6654 24.916 33.3393 25.701 32.687 26.3543C32.0348 27.0077 31.2498 27.3338 30.332 27.3327H3.66536ZM16.9987 15.666L30.332 7.33268V3.99935L16.9987 12.3327L3.66536 3.99935V7.33268L16.9987 15.666Z" fill="#05A5FF" />
                                </svg> {/* Email Icon */}
                                <div className='C2wContactUsHome_selfInfo_contact_detail'>
                                    <div className='C2wContactUsHome_selfInfo_label'>Email</div>
                                    <div className='C2wContactUsHome_selfInfo_contact'>Core2webinfo@gmail.com</div>
                                </div>
                            </div>

                            {/* Phone Section */}
                            <div className='C2wContactUsHome_selfInfo_contact_container'>
                                <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20.0964 15.8589L16.9934 15.5045C16.6285 15.4616 16.2587 15.502 15.9117 15.6227C15.5646 15.7434 15.2495 15.9411 14.9898 16.2012L12.7419 18.4501C9.27409 16.6851 6.45527 13.865 4.69109 10.3956L6.9512 8.1345C7.47652 7.60894 7.73307 6.87561 7.64755 6.13006L7.29326 3.05006C7.22426 2.45372 6.93832 1.90364 6.48997 1.50467C6.04161 1.1057 5.46218 0.885741 4.86213 0.886722H2.74862C1.36813 0.886722 0.219752 2.03561 0.305269 3.41672C0.952758 13.8545 9.29682 22.1901 19.7177 22.8378C21.0982 22.9234 22.2466 21.7745 22.2466 20.3934V18.2789C22.2588 17.0445 21.3303 16.0056 20.0964 15.8589Z" fill="#05A5FF" />
                                </svg>{/* Phone Icon */}
                                <div className='C2wContactUsHome_selfInfo_contact_detail'>
                                    <div className='C2wContactUsHome_selfInfo_label'>Phone number</div>
                                    <div className='C2wContactUsHome_selfInfo_contact'>7385092120 / 9665244353 / 9307922405</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Section: Contact Form */}
                    <div className='C2wContactUsHome_contactusForm'>
                        { windowWidth > 620 && <div className='C2wContactUsHome_contactusForm_heading'>Contact Us</div>} 
                        <div className='C2wContactUsHome_form_container'>
                            <form onSubmit={!isSuccess ? handleSubmit : null}>
                                {/* Name Input */}
                                <input
                                    type='text'
                                    name='name'
                                    placeholder='Name'
                                    value={formData.name}
                                    onChange={handleChange}
                                    style={{ border: errors.name ? "0.5px solid #ff000085" : null }}
                                />

                                {/* Email Input */}
                                <input
                                    type='text'
                                    name='email'
                                    placeholder='Email Id'
                                    value={formData.email}
                                    onChange={handleChange}
                                    style={{ border: errors.email ? "0.5px solid #ff000085" : null }}
                                />

                                {/* Phone Number Input */}
                                <input
                                    type='text'
                                    name='number'
                                    placeholder='Phone Number'
                                    value={formData.number}
                                    onChange={handleChange}
                                    style={{ border: errors.number ? "0.5px solid #ff000085" : null }}
                                />

                                {/* Course Dropdown */}
                                <div className='select_course' style={{ border: errors.courseIntrested ? "0.5px solid #ff000085" : null, color: formData.courseIntrested === '' ? "rgba(255, 255, 255, 0.4)" : null }}>
                                    <select
                                        name='courseIntrested'
                                        value={formData.courseIntrested}
                                        onChange={handleChange}
                                    >
                                        <option value="" style={{ color: "rgba(255, 255, 255, 0.4)" }} disabled>Interested for (Course)</option>
                                        {data?.coursesList.map((course) => <option value={course.value}>{course.label}</option>)}
                                    </select>
                                </div>

                                {/* Message Input */}
                                <input
                                    type='text'
                                    name='message'
                                    placeholder='Enquiry message'
                                    value={formData.message}
                                    onChange={handleChange}
                                    style={{ border: errors.message ? "0.5px solid red" : null }}
                                />

                                {/* Submit Button */}
                                <button type='submit' className='submitBtn'>
                                    {isSuccess ? <div className='btnLoader'></div> : "Submit"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Success Popup */}
                {popup && (
                    <div className='C2wrequest_popup home'>
                        <div className='C2wrequest_popup_container'>
                            <svg width="100" height="94" viewBox="0 0 100 94" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="49.0815" cy="46.0102" r="32.0883" fill="#00B64C" />
                                <path d="M60.983 36.3818C60.8251 36.2039 60.6372 36.0627 60.4301 35.9663C60.223 35.8699 60.001 35.8203 59.7766 35.8203C59.5523 35.8203 59.3302 35.8699 59.1232 35.9663C58.9161 36.0627 58.7282 36.2039 58.5702 36.3818L45.9115 50.5421L40.5932 44.5818C40.4292 44.4049 40.2356 44.2657 40.0234 44.1723C39.8113 44.0789 39.5848 44.0331 39.3568 44.0375C39.1288 44.0419 38.9038 44.0964 38.6947 44.198C38.4855 44.2995 38.2963 44.4461 38.1379 44.6293C37.9795 44.8125 37.8549 45.0288 37.7713 45.2658C37.6877 45.5028 37.6467 45.7558 37.6507 46.0105C37.6546 46.2652 37.7034 46.5166 37.7943 46.7502C37.8852 46.9838 38.0164 47.1952 38.1804 47.3721L44.7051 54.6611C44.8631 54.839 45.051 54.9802 45.2581 55.0766C45.4651 55.1729 45.6872 55.2225 45.9115 55.2225C46.1359 55.2225 46.3579 55.1729 46.565 55.0766C46.7721 54.9802 46.96 54.839 47.1179 54.6611L60.983 39.1721C61.1555 38.9943 61.2932 38.7786 61.3873 38.5385C61.4815 38.2984 61.5301 38.0391 61.5301 37.7769C61.5301 37.5148 61.4815 37.2555 61.3873 37.0154C61.2932 36.7753 61.1555 36.5595 60.983 36.3818Z" fill="white" />
                                <circle cx="13.778" cy="7.87368" r="7.87368" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="93.1719" cy="15.749" r="5.90526" fill="#00B64C" fill-opacity="0.2" />
                                <ellipse cx="3.93684" cy="69.8744" rx="3.93684" ry="3.60877" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="34.777" cy="91.2028" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="85.2985" cy="83.3356" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="89.235" cy="58.3903" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="56.4274" cy="3.93717" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                            </svg> {/* Success Icon */}
                            <h1>Registered Successfully</h1>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default C2wContactUsHome;

